import { miner } from '@cbre/digger';
import { withData, withIdRetrieval, WithIdRetrievalDef, withUoms, WithUomsDef } from '../base-composer';
import { withCompanyContacts, WithCompanyContactsDef } from '../company-contacts/company-contacts';
import { BaseEdp } from '../entity';
import { withGeography, WithGeographyDef } from '../geography/geography';

/**
 * Map with usage ref structure used to extract data from a usage record
 * using a miner
 *
 * @see miner
 */
const USAGE_TO_REF_MAPPING = {
  id: 'entity.id',
  total_direct_available_surface_area: 'total_direct_available_surface_area',
  total_available_area_sub_lease: 'total_available_area_sub_lease',
  total_available_surface_area: 'total_available_surface_area',
  direct_vacant_area: 'direct_vacant_area',
  sublease_vacant_area: 'sublease_vacant_area',
  cbre_managed_direct_surface_area_available: 'cbre_managed_direct_surface_area_available',
  total_vacant_area: 'total_vacant_area',
  cbre_managed_sub_lease_surface_area_available: 'cbre_managed_sub_lease_surface_area_available',
  ref_property_class_type_desc: 'ref_property_class_type_desc',
  ref_property_usage_type_desc: 'ref_property_usage_type_desc',
  ref_property_usage_sub_type_desc: 'ref_property_usage_sub_type_desc',
  lig_geography: 'lig_geography',
  statistical_f: 'statistical_f',
  number_tenants: 'number_tenants',
  maximum_clear_height: 'maximum_clear_height',
  minimum_clear_height: 'minimum_clear_height',
  bay_size_height: 'bay_size_height',
  number_dock_door: 'number_dock_door',
  number_truckwell_position: 'number_truckwell_position',
  cross_dock_position_exist_f: 'cross_dock_position_exist_f',
  refrigerator_area: 'refrigerator_area',
  available_freezer_area: 'available_freezer_area',
  bay_size_length: 'bay_size_length',
  bay_size_width: 'bay_size_width',
  voltage: 'voltage',
  amperage: 'amperage',
  office_area: 'office_area',
  ref_rail_service_type_desc: 'ref_rail_service_type_desc',
  grade_doors_dock_number: 'grade_doors_dock_number',
  ref_property_space_quality_type_desc: 'ref_property_space_quality_type_desc',
};

export interface WithTypeAndSubtypeDef {
  usageTypeAndSubtype: () => { usageType: string; usageSubtype: string };
  getUsageType: () => string;
  getUsageSubtype: () => string;
}

/**
 * Behavioral aggregation to return `{usageType, usageSubtype}` object
 */
const withTypeAndSubtype = (): WithTypeAndSubtypeDef => {
  function usageTypeAndSubtype() {
    return miner(this, {
      usageType: 'ref_property_usage_type_desc',
      usageSubtype: 'ref_property_usage_sub_type_desc',
    });
  }

  function getUsageType() {
    return this.ref_property_usage_type_desc;
  }

  function getUsageSubtype() {
    return this.ref_property_usage_type_desc;
  }

  return { usageTypeAndSubtype, getUsageType, getUsageSubtype };
};

export interface WithStatisticalMethodsDef {
  isStatistical(): () => boolean;
}

const withStatisticalMethods = (): WithStatisticalMethodsDef => {
  function isStatistical() {
    return this.statistical_f;
  }

  return { isStatistical };
};

export interface UsageDef
  extends BaseEdp, WithIdRetrievalDef, WithUomsDef, WithGeographyDef, WithCompanyContactsDef, WithTypeAndSubtypeDef {
  [key: string]: any;
}

/**
 * Usage Composed object
 * @param state Usage
 */
export const Usage = (state): UsageDef =>
  Object.assign(
    {},
    state,
    // composers
    withIdRetrieval(),
    withUoms(),
    withGeography(),
    withCompanyContacts(),
    withTypeAndSubtype(),
    withStatisticalMethods()
  );

export interface PropertyUsageDef
  extends WithIdRetrievalDef, WithUomsDef, WithGeographyDef, WithTypeAndSubtypeDef, WithStatisticalMethodsDef {
  property_usage: PropertyUsageDef;
}

/**
 * Usage subset (mainly used for availability, lease and sale objects)
 * @param state
 */
export const PropertyUsage = (state): PropertyUsageDef =>
  Object.assign(
    {},
    state,
    // composers
    withIdRetrieval(),
    withUoms(),
    withGeography(),
    withTypeAndSubtype(),
    withStatisticalMethods()
  );

/**
 * Transforms a composed or regular usage into a PropertyUsage composer,
 * which is a ref to an original usage with some minor differences.
 *
 * @param usage Usage composed or regular object to transform to a Ref
 */
export function transformUsageToRef(usage) {
  return PropertyUsage(Object.assign({}, usage, withData()).getData(USAGE_TO_REF_MAPPING));
}

export enum UsageTypes {
  Industrial = 'Industrial',
  Office = 'Office',
  Retail = 'Retail',
  Land = 'Land',
}
